import React, { useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { Field, useForm } from "react-final-form";
import PlotlyPlot from "react-plotly.js";
import Select from "components/Controls/Select";
import TextTruncate from "components/TextTruncate";
import HtmlText from "components/HtmlText";
import { fetchingSimulationPreviewDataSelector, simulationPreviewDataSelector, workspaceCurrentSimulationIdSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { PREVIEW_LINE_COLORS } from "store/rs-workspace/utils/scenario.utils";
import { setSimulationPreviewVariables } from "store/rs-workspace/actions/rs-workspace.actions";
import { defaultGridColor, defaultPlotConfig, defaultTickFont } from "shared/plotly/defaultConfigs";
import { useRoundTickValueObserver } from "shared/plotly/hooks";
import { isNotEmpty } from "utils/validate";
import { SimulationFormFields } from "../..";
import SpinnerIcon from "icons/spinner-yellow.svg";
import InfoIcon from "icons/info-red.svg";
import * as styles from "./styles.module.scss";
import * as colors from "styles/colorValues.module.scss";
import * as fonts from "styles/fontValues.module.scss";
import * as spinnerStyles from "components/Loader/styles.module.scss";
const defaultAxisLayout = {
    color: defaultGridColor,
    gridcolor: defaultGridColor,
    zerolinecolor: colors.black,
    tickfont: {
        ...defaultTickFont,
        size: 11,
    },
};
const plotConfig = {
    ...defaultPlotConfig,
    staticPlot: true,
};
const getBasicPlotLayout = () => ({
    autosize: true,
    height: 300,
    showlegend: false,
    dragmode: false,
    margin: {
        l: 52,
        r: 58,
        b: 37,
        t: 10,
    },
    xaxis: {
        ...defaultAxisLayout,
        tickmode: "auto",
        tickwidth: 1,
        tickcolor: colors.white,
        titlefont: {
            family: fonts.roboto,
            size: 14,
            color: colors.black,
        },
    },
    yaxis: {
        ...defaultAxisLayout,
        position: -0.2,
        tickangle: -45,
        ticksuffix: " ",
        showgrid: false,
        linecolor: PREVIEW_LINE_COLORS?.[0],
        tickcolor: PREVIEW_LINE_COLORS?.[0],
        tickformat: "000",
    },
    yaxis2: {
        ...defaultAxisLayout,
        tickangle: -45,
        ticksuffix: " ",
        overlaying: "y",
        side: "left",
        linecolor: PREVIEW_LINE_COLORS?.[1],
        tickcolor: PREVIEW_LINE_COLORS?.[1],
        tickformat: "000",
    },
    yaxis3: {
        ...defaultAxisLayout,
        tickangle: 45,
        tickprefix: " ",
        overlaying: "y",
        side: "left",
        showgrid: false,
        linecolor: PREVIEW_LINE_COLORS?.[2],
        tickcolor: PREVIEW_LINE_COLORS?.[2],
        tickformat: "000",
    },
    yaxis4: {
        ...defaultAxisLayout,
        tickangle: 45,
        tickprefix: " ",
        overlaying: "y",
        side: "right",
        showgrid: false,
        linecolor: PREVIEW_LINE_COLORS?.[2],
        tickcolor: PREVIEW_LINE_COLORS?.[2],
        tickformat: "000",
    },
    yaxis5: {
        ...defaultAxisLayout,
        tickangle: 45,
        tickprefix: " ",
        overlaying: "y",
        side: "right",
        showgrid: false,
        linecolor: PREVIEW_LINE_COLORS?.[2],
        tickcolor: PREVIEW_LINE_COLORS?.[2],
        tickformat: "000",
    },
    yaxis6: {
        ...defaultAxisLayout,
        tickangle: 45,
        tickprefix: " ",
        overlaying: "y",
        side: "right",
        showgrid: false,
        linecolor: PREVIEW_LINE_COLORS?.[2],
        tickcolor: PREVIEW_LINE_COLORS?.[2],
        tickformat: "000",
    },
});
const PreviewGraph = () => {
    const dispatch = useDispatch();
    const form = useForm();
    const plotRef = useRef(null);
    const { lines = [], unit, selectedVariables = [], variablesList = [], serverError, } = useSelector(simulationPreviewDataSelector);
    const currentSimulationId = useSelector(workspaceCurrentSimulationIdSelector);
    const fetching = useSelector(fetchingSimulationPreviewDataSelector(currentSimulationId ?? -1));
    const hasNoLines = useMemo(() => !lines || lines?.length === 0, [lines]);
    const renderNoDataMessage = useMemo(() => (React.createElement("p", { className: styles.noData }, "Configure simulation details to get a quick preview of data. To get accurate results run a full simulation from the results page.")), []);
    const plotLayout = useMemo(() => {
        const basicPlotLayout = getBasicPlotLayout();
        const { xaxis, yaxis, yaxis2, yaxis3 } = basicPlotLayout;
        return {
            ...basicPlotLayout,
            margin: {
                ...basicPlotLayout.margin,
                l: 26,
            },
            xaxis: {
                ...xaxis,
                automargin: true,
                title: unit ? `Time (${unit})` : "Time",
                domain: hasNoLines ? [0, 1] : [0.08 * (lines.length - 1), 1],
            },
            yaxis: {
                ...yaxis,
                tickcolor: hasNoLines ? styles.white : PREVIEW_LINE_COLORS?.[0],
                linecolor: hasNoLines ? styles.white : PREVIEW_LINE_COLORS?.[0],
                showticklabels: !hasNoLines,
            },
            yaxis2: {
                ...yaxis2,
                position: hasNoLines ? 0 : 0.08,
                visible: !hasNoLines,
            },
            yaxis3: {
                ...yaxis3,
                position: hasNoLines ? 0.08 : 0.16,
                visible: !hasNoLines,
            },
        };
    }, [hasNoLines, unit, lines.length]);
    const getVariableUnit = (baseUnit) => {
        let unit = "";
        const isHtmlUnit = !!baseUnit?.html;
        if (isHtmlUnit) {
            unit = `${baseUnit?.html}`;
        }
        if (!isHtmlUnit && !!baseUnit) {
            unit = baseUnit;
        }
        return unit;
    };
    const renderComplexVariable = useCallback((label, unit) => (React.createElement(React.Fragment, null,
        label,
        " ",
        !!unit && (React.createElement(React.Fragment, null,
            "(",
            React.createElement(HtmlText, { targetClassName: styles.unit, text: unit }),
            ")")))), []);
    const onVariableChange = useCallback((option, index) => {
        const updatedSelectedVariables = [...selectedVariables];
        updatedSelectedVariables.splice(index, 1, option);
        dispatch(setSimulationPreviewVariables({
            simulationId: currentSimulationId ?? -1,
            selectedVariables: updatedSelectedVariables,
        }));
        form.change(SimulationFormFields.variables, updatedSelectedVariables);
    }, [selectedVariables, dispatch, currentSimulationId, form]);
    const selectItemRenderer = useCallback((item, { handleClick }) => {
        const unit = getVariableUnit(item?.unit);
        return (React.createElement(TextTruncate, { key: item.value, className: classNames(styles.itemRenderer, { [styles.title]: item.isGroupName }), onClick: !item.isGroupName ? handleClick : () => null }, renderComplexVariable(item?.label, unit)));
    }, [renderComplexVariable]);
    const renderLegendItem = useCallback((variableOption, color) => {
        const variable = variablesList.find((item) => item.value === variableOption?.value);
        const unit = getVariableUnit(variable?.unit);
        return (React.createElement("div", { className: styles.legendItem },
            React.createElement("div", { className: styles.palette, style: { backgroundColor: color } }),
            renderComplexVariable(variable?.label, unit)));
    }, [variablesList, renderComplexVariable]);
    const renderVariableSelect = useCallback((variable, index) => {
        const otherSelectedVariables = selectedVariables.filter((item) => item?.value !== variable?.value);
        const items = variablesList.filter((item) => !otherSelectedVariables.some(({ value }) => value === item.value));
        const valueOption = variablesList.find((item) => item.value === variable?.value);
        const unit = getVariableUnit(valueOption?.unit);
        return (React.createElement("div", { key: `${variable}-${index}`, className: styles.field },
            React.createElement("p", { className: styles.title }, `Variable ${index + 1} *`),
            React.createElement(Select, { items: items, value: renderComplexVariable(valueOption?.label, unit), placeholder: "Select", itemRenderer: selectItemRenderer, onChange: (option) => onVariableChange(option, index), classes: {
                    root: classNames(styles.selectWrapper, {
                        [styles.empty]: !isNotEmpty(valueOption?.label),
                    }),
                    selectButton: styles.select,
                    portalClassName: styles.selectPortal,
                }, selectButtonProps: {
                    prefix: (React.createElement("div", { className: classNames(styles.palette, styles.absolute), style: { backgroundColor: PREVIEW_LINE_COLORS?.[index] } })),
                } })));
    }, [selectItemRenderer, variablesList, selectedVariables, onVariableChange, renderComplexVariable]);
    useRoundTickValueObserver(plotRef);
    return (React.createElement("div", { className: styles.root },
        React.createElement(Field, { name: SimulationFormFields.variables }, () => null),
        React.createElement("div", { className: styles.graph },
            !hasNoLines && (React.createElement("div", { className: styles.legend },
                React.createElement("div", { className: styles.left },
                    renderLegendItem(selectedVariables[0], PREVIEW_LINE_COLORS?.[0]),
                    renderLegendItem(selectedVariables[1], PREVIEW_LINE_COLORS?.[1])),
                React.createElement("div", { className: styles.right }, renderLegendItem(selectedVariables[2], PREVIEW_LINE_COLORS?.[2])))),
            React.createElement(PlotlyPlot, { ref: plotRef, config: plotConfig, layout: plotLayout, data: lines, className: styles.plot }),
            hasNoLines && !fetching && renderNoDataMessage,
            fetching && React.createElement(SpinnerIcon, { className: classNames(spinnerStyles.spinner, styles.centered) })),
        React.createElement("div", { className: styles.variables }, selectedVariables.map(renderVariableSelect)),
        !!serverError && (React.createElement("div", { className: styles.error },
            React.createElement(InfoIcon, { className: styles.icon }),
            React.createElement("span", { className: styles.text }, serverError)))));
};
export default PreviewGraph;
